/**
 * New media plan — one page: the brief on the left, priced live on the right.
 *
 * Every edit re-prices the brief 500 ms later (useLiveQuote); the server
 * computes every number. Create sends exactly the body that was last quoted,
 * with that quote's fingerprint and the seller's reasons for flagged rates.
 * If prices moved in between, the server answers 409 with the new quote,
 * which replaces the old one for review (typed rates stay). The draft
 * autosaves in this browser (draftStorage); nothing reaches the server until
 * Create.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../ui';
import {
  PlanRevisionError,
  PricesChangedError,
  ReasonsRequiredError,
  createMediaPlan,
  newRequestId,
  resolveAdvertiser,
  updateAdvertiser,
} from '../../../services/mediaPlansApi';
import NewAdvertiserDialog, { type AdvertiserDialogState } from './NewAdvertiserDialog';
import type { AdvertiserRecord, CreatePlanResult, MediaPlanDraftDoc, MediaPlanReference, MediaPlanUnitKey } from './types';
import {
  DIRECT_AGENCY,
  OTHER_AGENCY,
  draftAgencyKey,
  draftToPlanInput,
  fieldId,
  newSegmentDraft,
  pricingNotesFor,
  validateDraft,
  type PlanDraft,
  type RateEdit,
  type SegmentDraft,
} from './planDraft';
import { planChecks } from './planChecks';
import { useLiveQuote } from './useLiveQuote';
import { storedDraftTitle, useDraftAutosave, type StoredDraft } from './draftStorage';
import { useGoogleConnection } from './googleWorkspace';
import { bannerStyle, fmtBudget, fmtDateTime, fmtInt } from './formStyles';
import BuilderLayout from './builder/BuilderLayout';
import WhoSection from './builder/WhoSection';
import CampaignSection from './builder/CampaignSection';
import UnitsSection from './builder/UnitsSection';
import AudiencesSection from './builder/AudiencesSection';
import RatesSection from './builder/RatesSection';
import AddedValueSection from './builder/AddedValueSection';
import PlanLinesSection from './builder/PlanLinesSection';
import LiveSummary from './builder/LiveSummary';
import type { ErrorFor } from './builder/fields';

export type Notify = (kind: 'success' | 'error', title: string, message?: string) => void;

/** How a builder opens: the draft to start from, and where it came from. */
export interface BuilderBoot {
  /** Remounts the builder for each boot. */
  key: number;
  draft: PlanDraft;
  revisesPlanId?: string;
  revisesLabel?: string;
  /** Opened from a Culture Genius chat draft. */
  chatDraft?: MediaPlanDraftDoc;
  /** The chat draft a restored draft came from (its doc isn't kept). */
  chatDraftId?: string;
  /** The draft came back from this browser (saved then). */
  restoredAt?: number;
  /** A different unsaved draft this one would overwrite: autosave waits until the seller says so. */
  replaces?: StoredDraft;
}

interface Props {
  boot: BuilderBoot;
  uid: string | null;
  reference: MediaPlanReference;
  advertisers: AdvertiserRecord[];
  canManagePlans: boolean;
  onBack: () => void;
  onCreated: (result: CreatePlanResult) => void;
  /** The draft was discarded from the summary. */
  onDiscarded: () => void;
  /** "Start over" on a restored draft: a blank builder. */
  onStartOver: () => void;
  /** Open the other unsaved draft instead of this one. */
  onOpenStored: (stored: StoredDraft) => void;
  onOpenPlan: (planId: string) => void;
  onAdvertiserCreated: (advertiser: AdvertiserRecord) => void;
  onAdvertiserUpdated?: (advertiser: AdvertiserRecord) => void;
  notify: Notify;
}

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Something went wrong.');

const withoutKey = <T extends object>(obj: T, key: keyof T): T => {
  const next = { ...obj };
  delete next[key];
  return next;
};

const PlanBuilder: React.FC<Props> = ({
  boot,
  uid,
  reference,
  advertisers,
  canManagePlans,
  onBack,
  onCreated,
  onDiscarded,
  onStartOver,
  onOpenStored,
  onOpenPlan,
  onAdvertiserCreated,
  onAdvertiserUpdated,
  notify,
}) => {
  const [draft, setDraft] = useState<PlanDraft>(boot.draft);
  const [edited, setEdited] = useState(false);
  const [mayReplace, setMayReplace] = useState(!boot.replaces);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [tierIndex, setTierIndex] = useState(0);
  const [dialog, setDialog] = useState<AdvertiserDialogState | null>(null);
  const [addingName, setAddingName] = useState<string | null>(null);
  const [agencyTouched, setAgencyTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<React.ReactNode>(null);
  const [pricesChanged, setPricesChanged] = useState(false);
  const [createSheet, setCreateSheet] = useState(true);
  const [confirmingCategory, setConfirmingCategory] = useState(false);
  const google = useGoogleConnection();
  const requestIdFor = useRef<{ key: string; id: string } | null>(null);
  const mounted = useRef(true);
  // Set on every mount: StrictMode runs this cleanup once on its simulated unmount in development.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const agencies = reference.agencies.agencies;
  const categories = reference.categories.categories;
  const units = reference.units;

  // --- draft edits --------------------------------------------------------------------
  const change = useCallback((fn: (d: PlanDraft) => PlanDraft) => {
    setDraft(fn);
    setEdited(true);
    setPricesChanged(false);
    setCreateError(null);
  }, []);
  const update = useCallback((patch: Partial<PlanDraft>) => change((d) => ({ ...d, ...patch })), [change]);
  const setSegment = useCallback(
    (id: string, patch: Partial<SegmentDraft>) => change((d) => ({ ...d, segments: d.segments.map((s) => (s.id === id ? { ...s, ...patch } : s)) })),
    [change],
  );
  const addSegment = useCallback(() => change((d) => ({ ...d, segments: [...d.segments, newSegmentDraft(`Audience ${d.segments.length + 1}`)] })), [change]);
  const removeSegment = useCallback(
    (id: string) =>
      change((d) => ({
        ...d,
        segments: d.segments.filter((s) => s.id !== id),
        reasons: Object.fromEntries(Object.entries(d.reasons).filter(([key]) => key.split(':')[1] !== id)),
      })),
    [change],
  );
  const setRate = useCallback(
    (segmentId: string, unitKey: MediaPlanUnitKey, edit: RateEdit | null) =>
      change((d) => ({
        ...d,
        segments: d.segments.map((s) => (s.id !== segmentId ? s : { ...s, rates: edit ? { ...s.rates, [unitKey]: edit } : withoutKey(s.rates, unitKey) })),
      })),
    [change],
  );
  const setReason = useCallback((key: string, text: string) => change((d) => ({ ...d, reasons: { ...d.reasons, [key]: text } })), [change]);

  // --- pricing --------------------------------------------------------------------------
  const issues = useMemo(() => validateDraft(draft), [draft]);
  const input = useMemo(() => (issues.length ? null : draftToPlanInput(draft)), [draft, issues]);
  const live = useLiveQuote(input);
  const current = live.status === 'current' ? live.quote : null;
  const checks = useMemo(
    () => planChecks({ draft, issues, status: live.status, needsAdvertiser: live.needsAdvertiser, ambiguous: live.ambiguous, quote: current }),
    [draft, issues, live.status, live.needsAdvertiser, live.ambiguous, current],
  );
  const canCreate = !!current && !creating && checks.every((c) => c.tone !== 'fix');

  // --- field errors: shown once a field has been left, or a check pointed at it ----------
  const firstIssue = useMemo(() => {
    const byField = new Map<string, string>();
    for (const issue of issues) if (!byField.has(issue.field)) byField.set(issue.field, issue.message);
    return byField;
  }, [issues]);
  const errorFor: ErrorFor = useCallback((field) => (touched.has(field) ? firstIssue.get(field) : undefined), [touched, firstIssue]);
  const touch = useCallback((field: string) => setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field))), []);
  const onBlurCapture = (e: React.FocusEvent) => {
    const id = (e.target as HTMLElement).id;
    if (id.startsWith('mp-')) touch(id.slice(3));
  };
  const goTo = useCallback(
    (field: string) => {
      touch(field);
      const el = document.getElementById(fieldId(field));
      if (!el) return;
      el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      el.focus({ preventScroll: true });
    },
    [touch],
  );

  // --- autosave ---------------------------------------------------------------------------
  const autosave = useDraftAutosave(
    uid,
    edited && mayReplace ? { draft, revisesPlanId: boot.revisesPlanId, revisesLabel: boot.revisesLabel, chatDraftId: boot.chatDraft?.id ?? boot.chatDraftId } : null,
    { initialSavedAt: boot.restoredAt ?? null },
  );

  // --- advertiser -----------------------------------------------------------------------
  const pickAdvertiser = useCallback(
    (record: Pick<AdvertiserRecord, 'key' | 'name' | 'agencyKey'>) =>
      change((d) => ({
        ...d,
        advertiserKey: record.key,
        advertiserName: record.name,
        // The agency on file, unless the seller already chose one.
        ...(agencyTouched ? {} : { agencyKey: draftAgencyKey(record.agencyKey), agencyOther: '' }),
      })),
    [change, agencyTouched],
  );
  const pickByKey = useCallback(
    (key: string, name: string) => {
      setDialog(null);
      pickAdvertiser(advertisers.find((a) => a.key === key) ?? { key, name });
    },
    [advertisers, pickAdvertiser],
  );
  const addAdvertiser = useCallback(
    async (name: string) => {
      setAddingName(name);
      const agencyName = draft.agencyKey === OTHER_AGENCY ? draft.agencyOther : agencies.find((a) => a.key === draft.agencyKey)?.name;
      try {
        const outcome = await resolveAdvertiser(name, agencyName);
        if (!mounted.current) return;
        if (outcome.kind === 'existing') {
          pickAdvertiser(outcome.advertiser);
          notify('success', `${outcome.advertiser.name} is already in the advertiser list`);
        } else if (outcome.kind === 'ambiguous') {
          setDialog({ kind: 'ambiguous', name, error: `“${name}” matches more than one advertiser.`, candidates: outcome.candidates });
        } else {
          setDialog({ kind: 'needsAdvertiser', needs: outcome.needsAdvertiser });
        }
      } catch (err) {
        if (mounted.current) notify('error', `Couldn't look up ${name}`, errorMessage(err));
      } finally {
        if (mounted.current) setAddingName(null);
      }
    },
    [draft.agencyKey, draft.agencyOther, agencies, pickAdvertiser, notify],
  );
  const advertiserCreated = useCallback(
    (record: AdvertiserRecord) => {
      setDialog(null);
      onAdvertiserCreated(record);
      notify('success', `${record.name} added to the advertiser list`);
      pickAdvertiser(record);
    },
    [onAdvertiserCreated, notify, pickAdvertiser],
  );
  const picked = draft.advertiserKey ? advertisers.find((a) => a.key === draft.advertiserKey) : undefined;
  const confirmCategory = useCallback(async () => {
    if (!picked) return;
    setConfirmingCategory(true);
    try {
      const updated = await updateAdvertiser(picked.key, { categoryStatus: 'confirmed' });
      if (!mounted.current) return;
      onAdvertiserUpdated?.(updated);
      notify('success', 'Category confirmed');
      live.retry();
    } catch (err) {
      if (mounted.current) notify('error', 'Could not confirm the category', errorMessage(err));
    } finally {
      if (mounted.current) setConfirmingCategory(false);
    }
  }, [picked, onAdvertiserUpdated, notify, live]);

  // --- create -----------------------------------------------------------------------------
  const create = useCallback(async () => {
    if (!canCreate || !current || !live.quotedInput) return;
    const body = live.quotedInput;
    const key = JSON.stringify(body);
    // One id per body: a retry after a lost response returns the plan already made.
    if (requestIdFor.current?.key !== key) requestIdFor.current = { key, id: newRequestId() };
    setCreating(true);
    setCreateError(null);
    setPricesChanged(false);
    try {
      const result = await createMediaPlan(body, {
        createSheet: createSheet && google.connected === true,
        revisesPlanId: boot.revisesPlanId,
        requestId: requestIdFor.current.id,
        quoteFingerprint: current.fingerprint,
        pricingNotes: pricingNotesFor(draft, current.guardrails ?? []),
      });
      if (!mounted.current) return;
      autosave.discard();
      onCreated(result);
    } catch (err) {
      if (!mounted.current) return;
      if (err instanceof PricesChangedError) {
        live.replaceQuote(err.quote, body);
        setPricesChanged(true);
      } else if (err instanceof ReasonsRequiredError) {
        // The server flagged rates this quote didn't: price again to show them.
        live.retry();
        setCreateError('A flagged rate still needs a reason (10–500 characters). The rates have been checked again.');
      } else if (err instanceof PlanRevisionError) {
        const newer = err.code === 'already_revised' ? err.newerPlanId : undefined;
        setCreateError(
          <span style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <span>{err.message}</span>
            {newer && (
              <Button size="sm" variant="subtle" onClick={() => onOpenPlan(newer)}>
                Open the newer version
              </Button>
            )}
          </span>,
        );
      } else {
        setCreateError(`Could not create the media plan: ${errorMessage(err)}`);
      }
    } finally {
      if (mounted.current) setCreating(false);
    }
  }, [canCreate, current, live, createSheet, google.connected, boot.revisesPlanId, draft, autosave, onCreated, onOpenPlan]);

  const discard = useCallback(() => {
    autosave.discard();
    onDiscarded();
  }, [autosave, onDiscarded]);

  // --- page -------------------------------------------------------------------------------
  const chatMoved = useMemo(() => {
    const quoted = boot.chatDraft?.quoted?.tiers[0];
    const now = current?.preview.tiers[0];
    if (edited || !quoted || !now) return null;
    return Math.abs(quoted.paidImpressions - now.totals.paidImpressions) >= 1 || Math.abs(quoted.paidCostUsd - now.totals.paidCostUsd) >= 0.01 ? { quoted, now: now.totals } : null;
  }, [boot.chatDraft, current, edited]);

  const header = (
    <>
      <div style={styles.titleRow}>
        <Button size="sm" variant="ghost" onClick={onBack}>
          ← All plans
        </Button>
        <h2 style={styles.title}>{boot.revisesPlanId ? 'New version' : 'New media plan'}</h2>
      </div>
      {boot.replaces && !mayReplace && (
        <div role="group" aria-label="Another unsaved draft" style={bannerStyle('warning')}>
          <span style={{ flex: 1 }}>
            You have another unsaved draft — {storedDraftTitle(boot.replaces, fmtBudget)}, saved {fmtDateTime(new Date(boot.replaces.savedAt).toISOString())}. This one isn't saved until you choose.
          </span>
          <Button size="sm" variant="subtle" onClick={() => onOpenStored(boot.replaces!)}>
            Open that draft
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMayReplace(true)}>
            Replace it with this one
          </Button>
        </div>
      )}
      {boot.revisesPlanId && (
        <div style={bannerStyle('info')}>
          New version of {boot.revisesLabel || 'an earlier plan'}. Creating it replaces that version — or, if it's approved, it stays approved until this one is.
        </div>
      )}
      {boot.chatDraft && (
        <div style={bannerStyle('info')}>
          <span>
            Opened from Culture Genius chat ({fmtDateTime(boot.chatDraft.createdAt)}). Check the brief and the rates, then create the plan.
            {chatMoved && ` Prices have moved since chat priced it: ${fmtInt(chatMoved.quoted.paidImpressions)} → ${fmtInt(chatMoved.now.paidImpressions)} paid impressions.`}
          </span>
        </div>
      )}
      {boot.restoredAt && !edited && (
        <div style={bannerStyle('info')}>
          <span style={{ flex: 1 }}>Picked up where you left off — this draft was saved {fmtDateTime(new Date(boot.restoredAt).toISOString())}.</span>
          <Button size="sm" variant="ghost" onClick={onStartOver}>
            Start over
          </Button>
        </div>
      )}
    </>
  );

  const disabled = creating;
  const form = (
    <div onBlurCapture={onBlurCapture} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <WhoSection
        draft={draft}
        advertisers={advertisers}
        categories={categories}
        agencies={agencies}
        onPickAdvertiser={pickAdvertiser}
        onTypeAdvertiser={(name) => update({ advertiserKey: '', advertiserName: name })}
        onAddAdvertiser={(name) => void addAdvertiser(name)}
        addingAdvertiser={addingName !== null}
        matchedName={!draft.advertiserKey && current ? current.advertiser.name : undefined}
        onAgencyChange={(agencyKey) => {
          setAgencyTouched(true);
          update({ agencyKey });
        }}
        onAgencyOtherChange={(agencyOther) => update({ agencyOther })}
        onConfirmCategory={canManagePlans ? () => void confirmCategory() : undefined}
        confirmingCategory={confirmingCategory}
        errorFor={errorFor}
        disabled={disabled}
      />
      <CampaignSection draft={draft} update={update} errorFor={errorFor} disabled={disabled} />
      <UnitsSection draft={draft} update={update} units={units} standardMix={live.quote?.rateCard.defaultUnitMix} errorFor={errorFor} disabled={disabled} />
      <AudiencesSection draft={draft} units={units} onSegmentChange={setSegment} onAddSegment={addSegment} onRemoveSegment={removeSegment} errorFor={errorFor} disabled={disabled} />
      <RatesSection
        draft={draft}
        quote={live.quote}
        updating={live.status !== 'current'}
        units={units}
        tierIndex={tierIndex}
        onRateChange={setRate}
        onReasonChange={setReason}
        onUpchargesModeChange={(upchargesMode) => update({ upchargesMode })}
        errorFor={errorFor}
        disabled={disabled}
      />
      <AddedValueSection draft={draft} update={update} units={units} policy={live.quote?.rateCard.snapshot.addedValue} errorFor={errorFor} disabled={disabled} />
      <PlanLinesSection quote={live.quote} updating={live.status !== 'current'} tierIndex={tierIndex} onTierChange={setTierIndex} />
    </div>
  );

  const summary = (
    <LiveSummary
      status={live.status}
      quote={live.quote}
      tierIndex={tierIndex}
      onTierChange={setTierIndex}
      checks={checks}
      onGoTo={goTo}
      error={live.error}
      onRetry={live.retry}
      needsAdvertiser={live.needsAdvertiser}
      onAddAdvertiser={() => live.needsAdvertiser && void addAdvertiser(live.needsAdvertiser.name)}
      addingAdvertiser={addingName !== null}
      ambiguous={live.ambiguous}
      onPickCandidate={pickByKey}
      pricesChanged={pricesChanged}
      createSheet={createSheet}
      onCreateSheetChange={setCreateSheet}
      google={google}
      canCreate={canCreate}
      creating={creating}
      onCreate={() => void create()}
      createError={createError}
      createLabel={boot.revisesPlanId ? 'Create new version' : 'Create media plan'}
      savedAt={autosave.savedAt}
      saveFailed={autosave.failed}
      onDiscard={discard}
    />
  );

  return (
    <>
      <BuilderLayout header={header} form={form} summary={summary} />
      <NewAdvertiserDialog
        state={dialog}
        categories={categories}
        agencies={agencies}
        defaultAgencyKey={draft.agencyKey === DIRECT_AGENCY || draft.agencyKey === OTHER_AGENCY ? undefined : draft.agencyKey}
        submitLabel="Add advertiser"
        onCreated={advertiserCreated}
        onUseExisting={pickByKey}
        onCancel={() => setDialog(null)}
      />
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  titleRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title: { margin: 0, fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--fg-1)' },
};

export default PlanBuilder;
